import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Trash2 } from 'lucide-react';
import { formatCurrency } from '../utils/formatCurrency';

const EditInvoiceDialog = ({ open, onOpenChange, invoice, onSave }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [periodOfPerformance, setPeriodOfPerformance] = useState('');
  const [taxRate, setTaxRate] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      setItems(invoice.items?.map(item => ({
        description: item.description || '',
        quantity: item.quantity || 1,
        unit_price: item.unit_price || 0,
        unit: item.unit || 'hours'
      })) || [{ description: '', quantity: 1, unit_price: 0, unit: 'hours' }]);
      setNotes(invoice.notes || '');
      setPeriodOfPerformance(invoice.period_of_performance || '');
      setTaxRate(invoice.tax_rate || 0);
    }
  }, [open, invoice]);

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0, unit: 'hours' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (parseFloat(taxRate) || 0) / 100;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate items
    const validItems = items.filter(item => item.description && item.quantity > 0 && item.unit_price > 0);
    if (validItems.length === 0) {
      return;
    }

    setLoading(true);
    try {
      await onSave({
        items: validItems.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price),
          unit: item.unit
        })),
        notes: notes || null,
        period_of_performance: periodOfPerformance || null,
        tax_rate: parseFloat(taxRate) || 0
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving invoice:', error);
    } finally {
      setLoading(false);
    }
  };

  const isPaid = invoice?.status === 'paid';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('invoices.editInvoice')}</DialogTitle>
          <DialogDescription>
            {t('invoices.editInvoiceDescription', { number: invoice?.invoice_number })}
          </DialogDescription>
        </DialogHeader>

        {isPaid ? (
          <div className="p-4 bg-amber-50 rounded-sm border border-amber-200">
            <p className="text-amber-800">{t('invoices.cannotEditPaid')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t('invoices.items')}</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItem}
                  className="rounded-sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {t('invoices.addItem')}
                </Button>
              </div>
              
              {items.map((item, index) => (
                <div key={index} className="p-3 bg-slate-50 rounded-sm border border-slate-200 space-y-2">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder={t('invoices.itemDescription')}
                        className="rounded-sm"
                        required
                      />
                    </div>
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="h-9 w-9 rounded-sm hover:bg-red-100"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">{t('invoices.quantity')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        className="rounded-sm font-mono"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('invoices.unit')}</Label>
                      <Input
                        value={item.unit}
                        onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                        className="rounded-sm"
                        placeholder="Stunden"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('invoices.unitPrice')} (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.unit_price}
                        onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                        className="rounded-sm font-mono"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tax Rate */}
            <div className="space-y-2">
              <Label htmlFor="taxRate">{t('invoices.taxRate')} (%)</Label>
              <Input
                id="taxRate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                className="rounded-sm font-mono w-32"
              />
            </div>

            {/* Period of Performance */}
            <div className="space-y-2">
              <Label htmlFor="periodOfPerformance">{t('invoices.periodOfPerformance')}</Label>
              <Input
                id="periodOfPerformance"
                value={periodOfPerformance}
                onChange={(e) => setPeriodOfPerformance(e.target.value)}
                className="rounded-sm"
                placeholder="z.B. Januar 2025"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{t('invoices.notes')}</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="rounded-sm"
                placeholder={t('invoices.notesPlaceholder')}
                rows={3}
              />
            </div>

            {/* Totals */}
            <div className="p-3 bg-slate-50 rounded-sm border border-slate-200 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('invoices.subtotal')}:</span>
                <span className="font-mono">{formatCurrency(calculateSubtotal())}</span>
              </div>
              {taxRate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t('invoices.tax')} ({taxRate}%):</span>
                  <span className="font-mono">{formatCurrency(calculateTax())}</span>
                </div>
              )}
              <div className="flex justify-between font-bold border-t border-slate-200 pt-1 mt-1">
                <span>{t('invoices.total')}:</span>
                <span className="font-mono">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-sm"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="rounded-sm bg-primary hover:bg-[#D62839]"
              >
                {loading ? t('common.loading') : t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditInvoiceDialog;
